import React, { useEffect, useState, useCallback } from 'react';
import Pager from '../components/Pager';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
  LineChart, Line, CartesianGrid,
} from 'recharts';
import {
  fetchCallSummary, fetchCallPerformance, fetchCallDaywise,
  fetchCallHourly, fetchCallNeverAttended, fetchCallHistory,
  fetchCallAgents, fetchSimCallAudio,
} from '../services/api';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtSecs = (s: number) => {
  if (!s) return '—';
  const m = Math.floor(s / 60), sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};
const fmtHrs = (s: number) => {
  if (!s) return '0h';
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};
const fmtTimeAgo = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m ago`;
  return `${Math.floor(h / 24)}d ago`;
};
const initials = (name: string) =>
  name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

const HOUR_LABELS: Record<number, string> = {
  9:'9-10', 10:'10-11', 11:'11-12', 12:'12-1', 13:'1-2',
  14:'2-3', 15:'3-4', 16:'4-5', 17:'5-6', 18:'6-7', 19:'7-8',
};
const slotLabel = (h: number) => HOUR_LABELS[h] ?? `${h}:00`;

// ─── Filter bar ───────────────────────────────────────────────────────────────

type Preset = 'today' | 'yesterday' | 'last7' | 'last14' | 'last30' | 'custom';

type Filters = {
  preset: Preset;
  dateFrom: string;      // YYYY-MM-DD (only used when preset='custom')
  dateTo:   string;      // YYYY-MM-DD (only used when preset='custom')
  userId: string;
  businessHours: boolean;
  callStatus: string;
  direction: string;
};

// Formats a Date as local-time YYYY-MM-DD. Avoids toISOString() which
// converts to UTC and shifts the date by up to a day for IST users.
const ymd = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Translates preset (Today, Yesterday, Last N Days, or Custom) → concrete
// date_from / date_to strings that the backend baseQuery() understands.
// Fixes the old bug where 'Today' meant now->subDays(1)->startOfDay(),
// which was actually the last ~48h, not today.
const resolveDates = (f: Filters): { date_from: string; date_to: string } => {
  const today = new Date();
  const y = new Date(today); y.setDate(today.getDate() - 1);
  const daysAgo = (n: number) => { const d = new Date(today); d.setDate(today.getDate() - n); return d; };
  switch (f.preset) {
    case 'today':     return { date_from: ymd(today),        date_to: ymd(today) };
    case 'yesterday': return { date_from: ymd(y),            date_to: ymd(y) };
    case 'last7':     return { date_from: ymd(daysAgo(6)),   date_to: ymd(today) };
    case 'last14':    return { date_from: ymd(daysAgo(13)),  date_to: ymd(today) };
    case 'last30':    return { date_from: ymd(daysAgo(29)),  date_to: ymd(today) };
    case 'custom':    return { date_from: f.dateFrom,        date_to: f.dateTo };
  }
};

// Builds the query params every tab endpoint accepts. Central so no tab can
// forget a filter (the earlier bug — direction was defined but never sent).
export const buildCallReportParams = (f: Filters): Record<string, string | number | undefined> => {
  const { date_from, date_to } = resolveDates(f);
  return {
    date_from:      date_from || undefined,
    date_to:        date_to   || undefined,
    user_id:        f.userId  || undefined,
    direction:      f.direction  || undefined,
    call_status:    f.callStatus || undefined,
    business_hours: f.businessHours ? 1 : 0,
  };
};

const DEFAULT_FILTERS: Filters = {
  preset: 'last7',
  dateFrom: ymd(new Date(Date.now() - 6 * 86400 * 1000)),
  dateTo:   ymd(new Date()),
  userId: '',
  businessHours: false,
  callStatus: '',
  direction: '',
};

const FilterBar: React.FC<{ filters: Filters; agents: any[]; onChange: (f: Filters) => void; onApply: () => void }> = ({
  filters, agents, onChange, onApply,
}) => {
  // When switching to a preset, seed dateFrom/dateTo so 'custom' picks up
  // sensible defaults if the user clicks it right after.
  const handlePreset = (preset: Preset) => {
    const next: Filters = { ...filters, preset };
    if (preset !== 'custom') {
      const { date_from, date_to } = resolveDates(next);
      next.dateFrom = date_from;
      next.dateTo   = date_to;
    }
    onChange(next);
  };

  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 20 }}>
      <select className="filter-select" value={filters.preset} onChange={e => handlePreset(e.target.value as Preset)}>
        <option value="today">Today</option>
        <option value="yesterday">Yesterday</option>
        <option value="last7">Last 7 Days</option>
        <option value="last14">Last 14 Days</option>
        <option value="last30">Last 30 Days</option>
        <option value="custom">Custom Range</option>
      </select>

      {filters.preset === 'custom' && (
        <>
          <input
            type="date"
            className="filter-select"
            value={filters.dateFrom}
            max={filters.dateTo || undefined}
            onChange={e => onChange({ ...filters, dateFrom: e.target.value })}
          />
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>to</span>
          <input
            type="date"
            className="filter-select"
            value={filters.dateTo}
            min={filters.dateFrom || undefined}
            onChange={e => onChange({ ...filters, dateTo: e.target.value })}
          />
        </>
      )}

      <select className="filter-select" value={filters.userId} onChange={e => onChange({ ...filters, userId: e.target.value })}>
        <option value="">All Employees</option>
        {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
      </select>
      <select className="filter-select" value={filters.direction} onChange={e => onChange({ ...filters, direction: e.target.value })}>
        <option value="">All Types</option>
        <option value="incoming">Incoming</option>
        <option value="outgoing">Outgoing</option>
      </select>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
        <input type="checkbox" checked={filters.businessHours} onChange={e => onChange({ ...filters, businessHours: e.target.checked })} />
        Business Hrs 10AM–7PM
      </label>
      <button className="btn btn-primary" style={{ marginLeft: 'auto' }} onClick={onApply}>Apply</button>
      <button className="btn btn-ghost" onClick={() => onChange({ ...DEFAULT_FILTERS })}>Reset</button>
    </div>
  );
};

// ─── Avatar ───────────────────────────────────────────────────────────────────

const COLORS = ['#7c3aed','#3b82f6','#10b981','#f59e0b','#ec4899','#06b6d4','#8b5cf6'];
const Avatar: React.FC<{ name: string; size?: number; idx?: number }> = ({ name, size = 32, idx = 0 }) => (
  <div style={{
    width: size, height: size, borderRadius: '50%', background: COLORS[idx % COLORS.length],
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: size * 0.35, fontWeight: 700, flexShrink: 0,
  }}>{initials(name)}</div>
);

// ─── Tab: Summary ─────────────────────────────────────────────────────────────

const SummaryTab: React.FC<{ filters: Filters; agents: any[] }> = ({ filters }) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchCallSummary(buildCallReportParams(filters))
      .then(setData).finally(() => setLoading(false));
  }, [filters]);

  if (loading) return <div className="spinner">Loading…</div>;
  if (!data) return null;

  const { breakdown, quick_stats, employees } = data;
  const total = breakdown.total || 1;

  const pieData = [
    { name: 'Incoming', value: breakdown.incoming, color: '#22c55e' },
    { name: 'Outgoing', value: breakdown.outgoing, color: '#f59e0b' },
    { name: 'Missed',   value: breakdown.missed,   color: '#ef4444' },
    { name: 'Rejected', value: breakdown.rejected, color: '#6b7280' },
  ];

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
        {/* Call Type Breakdown */}
        <div className="data-card" style={{ padding: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>Call Type Breakdown</div>
          {pieData.map(p => (
            <div key={p.name} style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color, display: 'inline-block', marginRight: 8 }} />
              <span style={{ flex: 1, fontSize: 13 }}>{p.name}</span>
              <span style={{ fontWeight: 700, fontSize: 14, marginRight: 12 }}>{p.value.toLocaleString('en-IN')}</span>
              <span style={{ color: 'var(--text-muted)', fontSize: 12, width: 45, textAlign: 'right' }}>
                {((p.value / total) * 100).toFixed(1)}%
              </span>
            </div>
          ))}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 10, display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
            <span>Total</span><span>{breakdown.total.toLocaleString('en-IN')}</span>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="data-card" style={{ padding: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>Quick Stats</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[
              { label: 'Never Attended', value: quick_stats.never_attended, color: '#ef4444', bg: '#fee2e2' },
              { label: 'Not Answered',   value: quick_stats.not_answered,   color: '#f59e0b', bg: '#fef3c7' },
              { label: 'Connected',      value: quick_stats.connected,      color: '#22c55e', bg: '#dcfce7' },
              { label: 'Unique Clients', value: quick_stats.unique_clients, color: '#3b82f6', bg: '#dbeafe' },
              { label: 'Total Work',     value: fmtHrs(quick_stats.total_seconds), color: '#7c3aed', bg: '#ede9fe' },
              { label: 'Avg Call Dur',   value: fmtSecs(quick_stats.avg_seconds),  color: '#0891b2', bg: '#cffafe' },
            ].map(s => (
              <div key={s.label} style={{ background: s.bg, borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Donut chart (SVG) */}
        <div className="data-card" style={{ padding: 20, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Call Ratios</div>
          <svg viewBox="0 0 100 100" width={120} height={120}>
            {(() => {
              let offset = 0;
              const circumference = 2 * Math.PI * 35;
              return pieData.map(p => {
                const pct = p.value / total;
                const dash = pct * circumference;
                const el = (
                  <circle key={p.name} cx="50" cy="50" r="35" fill="none"
                    stroke={p.color} strokeWidth="14"
                    strokeDasharray={`${dash} ${circumference - dash}`}
                    strokeDashoffset={-offset}
                    style={{ transform: 'rotate(-90deg)', transformOrigin: '50% 50%' }}
                  />
                );
                offset += dash;
                return el;
              });
            })()}
            <text x="50" y="50" textAnchor="middle" dominantBaseline="middle" fontSize="14" fontWeight="800">{breakdown.total}</text>
            <text x="50" y="62" textAnchor="middle" dominantBaseline="middle" fontSize="7" fill="#94a3b8">total calls</text>
          </svg>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', marginTop: 8, width: '100%' }}>
            {pieData.map(p => (
              <div key={p.name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: p.color, flexShrink: 0 }} />
                <span style={{ color: 'var(--text-muted)' }}>{p.name}</span>
                <span style={{ fontWeight: 700, marginLeft: 'auto' }}>{p.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Employee Productivity */}
      <div className="data-card">
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', fontWeight: 700, fontSize: 14 }}>
          Employee Productivity <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 12 }}>· {employees.length} employees · sorted by total calls</span>
        </div>
        <div className="data-card-wrap">
          <table>
            <thead>
              <tr>
                <th>#</th><th>Employee</th><th>Total</th><th>Connected</th>
                <th>Conn Dur</th><th>Avg Dur</th><th>Unique</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((e: any, i: number) => (
                <tr key={e.user_id}>
                  <td className="td-muted">{i + 1}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Avatar name={e.agent_name} idx={i} />
                      <span style={{ fontWeight: 600 }}>{e.agent_name}</span>
                    </div>
                  </td>
                  <td style={{ fontWeight: 700 }}>{e.total}</td>
                  <td>{e.connected}</td>
                  <td className="td-muted">{fmtSecs(e.conn_seconds)}</td>
                  <td className="td-muted">{fmtSecs(e.avg_seconds)}</td>
                  <td className="td-muted">{e.unique_clients}</td>
                </tr>
              ))}
              {employees.length === 0 && (
                <tr className="table-empty-row"><td colSpan={7}>No call data for this period.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ─── Tab: Performance ─────────────────────────────────────────────────────────

const PerformanceTab: React.FC<{ filters: Filters }> = ({ filters }) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchCallPerformance(buildCallReportParams(filters))
      .then(setData).finally(() => setLoading(false));
  }, [filters]);

  if (loading) return <div className="spinner">Loading…</div>;
  if (!data) return null;

  // Build hourly chart data (0-23)
  const hourMap: Record<number, any> = {};
  for (let h = 9; h <= 19; h++) hourMap[h] = { hour: slotLabel(h), incoming: 0, outgoing: 0, missed: 0 };
  data.by_hour.forEach((r: any) => {
    if (!hourMap[r.hour]) return;
    if (r.direction === 'incoming' && r.call_status === 'connected') hourMap[r.hour].incoming += r.cnt;
    else if (r.direction === 'outgoing') hourMap[r.hour].outgoing += r.cnt;
    else if (['missed','rejected','busy'].includes(r.call_status)) hourMap[r.hour].missed += r.cnt;
  });
  const hourlyData = Object.values(hourMap);

  const avgs = data.avg_durations;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div className="data-card" style={{ padding: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14 }}>Calls by Hour Slot</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={hourlyData} barSize={10}>
              <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} width={30} />
              <Tooltip />
              <Legend iconSize={10} />
              <Bar dataKey="incoming" fill="#22c55e" name="Incoming" />
              <Bar dataKey="outgoing" fill="#f59e0b" name="Outgoing" />
              <Bar dataKey="missed"   fill="#ef4444" name="Missed" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="data-card" style={{ padding: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14 }}>Average Durations</div>
          {[
            { label: 'Avg Incoming', val: avgs?.avg_incoming },
            { label: 'Avg Outgoing', val: avgs?.avg_outgoing },
            { label: 'Avg Overall',  val: avgs?.avg_overall },
            { label: 'Total Talk',   val: avgs?.total_s, fmt: fmtHrs },
          ].map(d => (
            <div key={d.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{d.label}</span>
              <span style={{ fontWeight: 700, color: 'var(--navy)' }}>{d.fmt ? d.fmt(d.val ?? 0) : fmtSecs(d.val ?? 0)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Mobile Call Volume + Duration Records */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div className="data-card" style={{ padding: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14 }}>Mobile Call Volume</div>
          {[
            { label: 'Top Dialer',   agent: data.top.topDialer,   sub: 'outgoing', color: '#f59e0b' },
            { label: 'Top Answered', agent: data.top.topAnswered, sub: 'incoming', color: '#22c55e' },
            { label: 'Top Volume',   agent: data.top.topVolume,   sub: 'total',    color: '#7c3aed' },
          ].map((t, i) => t.agent && (
            <div key={t.label} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: t.color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>
                {i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{t.label}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t.agent.agent_name}</div>
              </div>
              <span style={{ fontWeight: 800, fontSize: 16, color: t.color }}>{t.agent.cnt}</span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t.sub}</span>
            </div>
          ))}
        </div>

        <div className="data-card" style={{ padding: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14 }}>Call Duration Records</div>
          {data.records.highestTotal && (
            <div style={{ background: '#fef3c7', borderRadius: 10, padding: 14, marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: '#92400e', fontWeight: 600, marginBottom: 4 }}>🏆 HIGHEST TOTAL DURATION</div>
              <div style={{ fontWeight: 700 }}>{data.records.highestTotal.agent_name}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#92400e' }}>{fmtSecs(data.records.highestTotal.total_s)}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>across {data.records.highestTotal.call_count} calls</div>
            </div>
          )}
          {data.records.longestCall && (
            <div style={{ background: '#ede9fe', borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 11, color: '#5b21b6', fontWeight: 600, marginBottom: 4 }}>⏱ LONGEST SINGLE CALL</div>
              <div style={{ fontWeight: 700 }}>{data.records.longestCall.agent_name}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#5b21b6' }}>{fmtSecs(data.records.longestCall.duration_seconds)}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{data.records.longestCall.called_phone}</div>
            </div>
          )}
        </div>
      </div>

      {/* Full Leaderboard */}
      <div className="data-card">
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', fontWeight: 700, fontSize: 14 }}>
          Full Leaderboard <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 12 }}>· Click row to drill down</span>
        </div>
        <div className="data-card-wrap">
          <table>
            <thead>
              <tr><th>Rank</th><th>Agent</th><th>Outgoing</th><th>Incoming</th><th>Total</th><th>Talk Time</th><th>Avg</th><th>Connect%</th></tr>
            </thead>
            <tbody>
              {data.leaderboard.map((e: any, i: number) => (
                <tr key={e.user_id}>
                  <td style={{ fontWeight: 700, color: i < 3 ? ['#f59e0b','#94a3b8','#92400e'][i] : 'inherit' }}>
                    {i < 3 ? ['🥇','🥈','🥉'][i] : i + 1}
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Avatar name={e.agent_name} idx={i} size={28} />
                      <span style={{ fontWeight: 600, fontSize: 13 }}>{e.agent_name}</span>
                    </div>
                  </td>
                  <td>{e.outgoing}</td>
                  <td>{e.incoming}</td>
                  <td style={{ fontWeight: 700 }}>{e.total}</td>
                  <td className="td-muted">{fmtSecs(e.talk_seconds)}</td>
                  <td className="td-muted">{fmtSecs(e.avg_seconds)}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ flex: 1, height: 6, background: '#e2e8f0', borderRadius: 3, maxWidth: 80 }}>
                        <div style={{ height: 6, width: `${Math.min(100, e.connect_pct ?? 0)}%`, background: '#22c55e', borderRadius: 3 }} />
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 600 }}>{e.connect_pct ?? 0}%</span>
                    </div>
                  </td>
                </tr>
              ))}
              {data.leaderboard.length === 0 && (
                <tr className="table-empty-row"><td colSpan={8}>No call data.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ─── Tab: Day-wise ────────────────────────────────────────────────────────────

const SLOTS = [9,10,11,12,13,14,15,16,17,18,19];

const DaywiseTab: React.FC<{ filters: Filters }> = ({ filters }) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchCallDaywise(buildCallReportParams(filters))
      .then(setData).finally(() => setLoading(false));
  }, [filters]);

  if (loading) return <div className="spinner">Loading…</div>;
  if (!data) return null;

  const { heatmap, daily_totals } = data;
  const maxVal = Math.max(1, ...heatmap.map((r: any) => r.total));

  // Build lookup: date → hour → data
  const lookup: Record<string, Record<number, any>> = {};
  heatmap.forEach((r: any) => {
    if (!lookup[r.date]) lookup[r.date] = {};
    lookup[r.date][r.hour] = r;
  });

  const dates = heatmap
    .map((r: any) => r.date as string)
    .filter((d: string, i: number, arr: string[]) => arr.indexOf(d) === i)
    .sort();

  // Trend chart data
  const trendData = daily_totals.map((d: any) => ({
    date: d.date.slice(5),
    Total: d.total,
    Connected: d.connected,
  }));

  const heatColor = (val: number) => {
    const pct = val / maxVal;
    if (pct === 0) return '#f8fafc';
    if (pct < 0.25) return '#d1fae5';
    if (pct < 0.5)  return '#6ee7b7';
    if (pct < 0.75) return '#10b981';
    return '#047857';
  };

  const dayLabel = (d: string) => {
    const dt = new Date(d);
    return `${['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][dt.getDay()]} ${dt.getDate()}-${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][dt.getMonth()]}`;
  };

  return (
    <div>
      <div className="data-card" style={{ marginBottom: 20, overflowX: 'auto' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', fontWeight: 700, fontSize: 14 }}>
          Day-wise Time Matrix
        </div>
        <div style={{ padding: 16 }}>
          <table style={{ fontSize: 12, borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600, color: 'var(--text-muted)' }}>DAY</th>
                <th style={{ padding: '6px 8px', fontWeight: 600 }}>TOTAL</th>
                <th style={{ padding: '6px 8px', fontWeight: 600, color: '#22c55e' }}>CONN</th>
                <th style={{ padding: '6px 8px', fontWeight: 600, color: 'var(--text-muted)' }}>DURATION</th>
                {SLOTS.map(h => (
                  <th key={h} style={{ padding: '6px 8px', fontWeight: 600, color: 'var(--text-muted)', minWidth: 44, textAlign: 'center' }}>
                    {slotLabel(h)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dates.map((date: string) => {
                const day = daily_totals.find((d: any) => d.date === date) ?? { total: 0, connected: 0, duration_s: 0 };
                return (
                  <tr key={date} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 10px', fontWeight: 600 }}>{dayLabel(date)}</td>
                    <td style={{ padding: '8px', textAlign: 'center', fontWeight: 700 }}>{day.total}</td>
                    <td style={{ padding: '8px', textAlign: 'center', color: '#22c55e', fontWeight: 700 }}>{day.connected}</td>
                    <td style={{ padding: '8px', textAlign: 'center', color: 'var(--text-muted)' }}>{fmtSecs(day.duration_s)}</td>
                    {SLOTS.map(h => {
                      const cell = lookup[date]?.[h];
                      const val = cell?.total ?? 0;
                      return (
                        <td key={h} style={{ padding: '4px', textAlign: 'center' }}>
                          <div style={{
                            background: heatColor(val), borderRadius: 4, padding: '5px 4px',
                            fontWeight: val > 0 ? 700 : 400, color: val / maxVal > 0.5 ? '#fff' : 'inherit',
                            fontSize: 12,
                          }}>
                            {val || ''}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {dates.length === 0 && (
                <tr><td colSpan={SLOTS.length + 4} style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>No data.</td></tr>
              )}
            </tbody>
          </table>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 12, color: 'var(--text-muted)' }}>
            <span>Heatmap Intensity:</span>
            {['#f8fafc','#d1fae5','#6ee7b7','#10b981','#047857'].map(c => (
              <div key={c} style={{ width: 20, height: 12, background: c, borderRadius: 2 }} />
            ))}
            <span>Low → High</span>
          </div>
        </div>
      </div>

      {trendData.length > 0 && (
        <div className="data-card" style={{ padding: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14 }}>Calls Trend Over Time</div>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} width={30} />
              <Tooltip />
              <Legend iconSize={10} />
              <Line type="monotone" dataKey="Total"     stroke="#7c3aed" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="Connected" stroke="#22c55e" strokeWidth={2} dot={false} strokeDasharray="5 5" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

// ─── Tab: Hourly ──────────────────────────────────────────────────────────────

const DENSITY_COLORS = ['#22c55e','#84cc16','#eab308','#f97316','#ef4444'];
const densityColor = (pct: number) => DENSITY_COLORS[Math.min(4, Math.floor(pct / 20))];

const HourlyTab: React.FC<{ filters: Filters }> = ({ filters }) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchCallHourly(buildCallReportParams(filters))
      .then(setData).finally(() => setLoading(false));
  }, [filters]);

  if (loading) return <div className="spinner">Loading…</div>;
  if (!data) return null;

  const peak = data.peak_hour;
  const chartData = data.hourly.filter((r: any) => r.hour >= 9 && r.hour <= 19).map((r: any) => ({
    slot: slotLabel(r.hour), total: r.total,
  }));

  return (
    <div>
      {peak && (
        <div style={{ background: '#fef3c7', borderRadius: 10, padding: '10px 16px', marginBottom: 16, fontSize: 13 }}>
          🔥 <strong>Peak hour: {slotLabel(peak.hour)}</strong> ({peak.pct_of_day?.toFixed(1)}% of all calls)
        </div>
      )}

      <div className="data-card" style={{ padding: 20, marginBottom: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14 }}>Hourly Call Distribution</div>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData}>
            <XAxis dataKey="slot" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} width={30} />
            <Tooltip />
            <Bar dataKey="total" radius={[4,4,0,0]} fill="#7c3aed" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="data-card">
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', fontWeight: 700, fontSize: 14 }}>Hour-by-Hour Breakdown</div>
        <div className="data-card-wrap">
          <table>
            <thead>
              <tr><th>Time Slot</th><th>Total</th><th>% of Day</th><th>Connected</th><th>Missed</th><th>Avg Dur</th><th style={{ minWidth: 140 }}>Density</th></tr>
            </thead>
            <tbody>
              {data.hourly.filter((r: any) => r.hour >= 9 && r.hour <= 19).map((r: any) => (
                <tr key={r.hour}>
                  <td style={{ fontWeight: 600 }}>{slotLabel(r.hour)}</td>
                  <td style={{ fontWeight: 700 }}>{r.total}</td>
                  <td className="td-muted">{r.pct_of_day?.toFixed(2)}%</td>
                  <td>{r.connected}</td>
                  <td style={{ color: r.missed > 0 ? '#ef4444' : 'inherit', fontWeight: r.missed > 0 ? 700 : 400 }}>{r.missed}</td>
                  <td className="td-muted">{fmtSecs(r.avg_s)}</td>
                  <td>
                    <div style={{ height: 8, background: '#e2e8f0', borderRadius: 4, width: '100%' }}>
                      <div style={{ height: 8, width: `${Math.min(100, r.pct_of_day ?? 0) * 7}%`, background: densityColor(r.pct_of_day ?? 0), borderRadius: 4 }} />
                    </div>
                  </td>
                </tr>
              ))}
              {data.hourly.length === 0 && (
                <tr className="table-empty-row"><td colSpan={7}>No call data.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ─── Tab: Never Attended ──────────────────────────────────────────────────────

const NeverAttendedTab: React.FC<{ filters: Filters }> = ({ filters }) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    fetchCallNeverAttended(buildCallReportParams(filters))
      .then(setData).finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="spinner">Loading…</div>;
  if (!data) return null;

  return (
    <div>
      {data.count > 0 && (
        <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: '14px 18px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 20 }}>⚠️</span>
          <div>
            <div style={{ fontWeight: 700, color: '#9a3412' }}>Unreturned Missed Calls: {data.count} leads leaking</div>
            <div style={{ fontSize: 12, color: '#c2410c' }}>These customers reached out but never received a call-back. Each unreturned call is potential lost revenue.</div>
          </div>
        </div>
      )}

      <div className="data-card">
        <div className="data-card-wrap">
          <table>
            <thead>
              <tr>
                <th>#</th><th>Assigned Agent</th><th>Customer</th>
                <th>Missed At</th><th>Total Attempts</th><th>After Miss</th><th>SLA</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((r: any, i: number) => (
                <tr key={r.id}>
                  <td className="td-muted">{i + 1}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Avatar name={r.agent_name ?? 'Unknown'} idx={i} size={28} />
                      <span style={{ fontSize: 13 }}>{r.agent_name ?? '—'}</span>
                    </div>
                  </td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{r.customer_phone}</div>
                    {r.lead_id && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Lead #{r.lead_id}</div>}
                  </td>
                  <td className="td-muted">{fmtTimeAgo(r.missed_at)}</td>
                  <td>
                    <span style={{ background: '#ede9fe', color: '#6d28d9', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
                      {r.total_attempts} attempts
                    </span>
                  </td>
                  <td style={{ fontWeight: 700 }}>{r.after_miss}</td>
                  <td>
                    <span style={{
                      background: r.sla_breached ? '#fee2e2' : '#dcfce7',
                      color: r.sla_breached ? '#991b1b' : '#166534',
                      borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 600,
                    }}>
                      {r.sla_breached ? '⚡ BREACH' : '✓ OK'}
                    </span>
                  </td>
                  <td>
                    <a href={`tel:${r.customer_phone}`} className="btn btn-primary" style={{ fontSize: 12, padding: '4px 12px', textDecoration: 'none' }}>
                      Call
                    </a>
                  </td>
                </tr>
              ))}
              {data.data.length === 0 && (
                <tr className="table-empty-row"><td colSpan={8}>No unreturned missed calls. Great job!</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ─── Tab: Call History ────────────────────────────────────────────────────────

const STATUS_STYLE: Record<string, { bg: string; color: string }> = {
  connected: { bg: '#dcfce7', color: '#166534' },
  missed:    { bg: '#fee2e2', color: '#991b1b' },
  rejected:  { bg: '#f1f5f9', color: '#475569' },
  busy:      { bg: '#fef3c7', color: '#92400e' },
};

const CallHistoryTab: React.FC<{ filters: Filters }> = ({ filters }) => {
  const [data, setData]       = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [page, setPage]       = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [playingId, setPlayingId]       = useState<number | null>(null);
  const [audioUrl, setAudioUrl]         = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    // Call History tab has its own call_status pill row that overrides the
    // global filter. Merge central params, then override call_status + add
    // search/page so the tab remains self-contained.
    fetchCallHistory({
      ...buildCallReportParams(filters),
      call_status: statusFilter || undefined,
      search: search || undefined,
      page,
    }).then(setData).finally(() => setLoading(false));
  }, [filters, search, statusFilter, page]);

  useEffect(() => { load(); }, [load]);

  const handlePlay = async (id: number) => {
    if (playingId === id) { setPlayingId(null); setAudioUrl(null); return; }
    try {
      const url = await fetchSimCallAudio(id);
      setAudioUrl(url); setPlayingId(id);
    } catch { alert('Recording not available'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          className="filter-select"
          style={{ flex: 1, minWidth: 200 }}
          placeholder="Search agent, customer, phone…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
        />
        <div style={{ display: 'flex', gap: 6 }}>
          {['', 'connected', 'missed', 'busy', 'rejected'].map(s => (
            <button
              key={s}
              onClick={() => { setStatusFilter(s); setPage(1); }}
              style={{
                padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
                background: statusFilter === s ? (s === '' ? '#7c3aed' : (STATUS_STYLE[s]?.bg ?? '#e2e8f0')) : '#f1f5f9',
                color: statusFilter === s ? (s === '' ? '#fff' : (STATUS_STYLE[s]?.color ?? 'inherit')) : 'var(--text-muted)',
              }}
            >
              {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {playingId && audioUrl && (
        <div style={{ background: '#ede9fe', borderRadius: 10, padding: '10px 16px', marginBottom: 12 }}>
          <audio controls autoPlay src={audioUrl} style={{ width: '100%', height: 36 }} onEnded={() => { setPlayingId(null); setAudioUrl(null); }} />
        </div>
      )}

      <div className="data-card">
        <div className="data-card-wrap">
          <table>
            <thead>
              <tr><th>#</th><th>Agent</th><th>Customer</th><th>When</th><th>Dir</th><th>Talk Time</th><th>Status</th><th>Rec</th></tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 24 }}>Loading…</td></tr>
              ) : data?.data.map((r: any, i: number) => (
                <tr key={r.id}>
                  <td className="td-muted">{((data.current_page - 1) * 30) + i + 1}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Avatar name={r.agent_name ?? 'Unknown'} idx={i} size={26} />
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{r.agent_name ?? '—'}</span>
                    </div>
                  </td>
                  <td>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{r.called_phone}</div>
                    {r.caller_phone && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{r.caller_phone}</div>}
                  </td>
                  <td>
                    <div style={{ fontSize: 13 }}>{new Date(r.started_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{new Date(r.started_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</div>
                  </td>
                  <td>
                    <span style={{
                      background: r.direction === 'incoming' ? '#dbeafe' : '#dcfce7',
                      color: r.direction === 'incoming' ? '#1d4ed8' : '#166534',
                      borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 700,
                    }}>
                      {r.direction === 'incoming' ? '↙ IN' : '↗ OUT'}
                    </span>
                  </td>
                  <td className="td-muted">{r.duration_seconds > 0 ? fmtSecs(r.duration_seconds) : '—'}</td>
                  <td>
                    <span style={{ borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600, ...(STATUS_STYLE[r.call_status] ?? { bg: '#f1f5f9', color: '#475569' }) }}>
                      {r.call_status.toUpperCase()}
                    </span>
                  </td>
                  <td>
                    {r.has_audio ? (
                      <button onClick={() => handlePlay(r.id)} title="Play recording"
                        style={{ background: playingId === r.id ? '#7c3aed' : '#ede9fe', border: 'none', borderRadius: '50%', width: 30, height: 30, cursor: 'pointer', fontSize: 14, color: playingId === r.id ? '#fff' : '#7c3aed' }}>
                        {playingId === r.id ? '⏹' : '▶'}
                      </button>
                    ) : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}
                  </td>
                </tr>
              ))}
              {!loading && (!data || data.data.length === 0) && (
                <tr className="table-empty-row"><td colSpan={8}>No calls found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {data && <Pager currentPage={page} lastPage={data.last_page} total={data.total} label="Calls" onChange={setPage} />}
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

type TabId = 'summary' | 'performance' | 'daywise' | 'hourly' | 'never-attended' | 'history';

const TABS: { id: TabId; label: string }[] = [
  { id: 'summary',        label: 'Summary' },
  { id: 'performance',    label: 'Performance' },
  { id: 'daywise',        label: 'Day-wise' },
  { id: 'hourly',         label: 'Hourly' },
  { id: 'never-attended', label: 'Never Attended' },
  { id: 'history',        label: 'Call History' },
];

const CallReportsPage: React.FC = () => {
  const [tab, setTab]     = useState<TabId>('summary');
  const [agents, setAgents] = useState<any[]>([]);
  const [filters, setFilters] = useState<Filters>({ ...DEFAULT_FILTERS });
  const [applied, setApplied] = useState<Filters>({ ...DEFAULT_FILTERS });

  useEffect(() => { fetchCallAgents().then(setAgents).catch(() => {}); }, []);

  return (
    <div className="list-page">
      <div className="page-hd">
        <div className="page-hd-left">
          <h1 className="page-title">Call Reports</h1>
          <p className="page-meta">Periodic reports — call volume, ratios, and team productivity</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--border)', marginBottom: 20 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer',
            fontWeight: 600, fontSize: 13,
            color: tab === t.id ? '#7c3aed' : 'var(--text-muted)',
            borderBottom: tab === t.id ? '2px solid #7c3aed' : '2px solid transparent',
            marginBottom: -2,
          }}>
            {t.label}
          </button>
        ))}
      </div>

      <FilterBar filters={filters} agents={agents} onChange={setFilters} onApply={() => setApplied({ ...filters })} />

      {tab === 'summary'        && <SummaryTab       filters={applied} agents={agents} />}
      {tab === 'performance'    && <PerformanceTab   filters={applied} />}
      {tab === 'daywise'        && <DaywiseTab       filters={applied} />}
      {tab === 'hourly'         && <HourlyTab        filters={applied} />}
      {tab === 'never-attended' && <NeverAttendedTab filters={applied} />}
      {tab === 'history'        && <CallHistoryTab   filters={applied} />}
    </div>
  );
};

export default CallReportsPage;
